import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { Link } from "react-router-dom";
import { Upload, Map, FileText, Image as ImageIcon, ArrowUpRight, FilePlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { can } from "@/lib/permissions";

const DISCIPLINES = [
  "Drainage","Roadworks","Pavement","Kerb","Concrete","Structures",
  "Services","Traffic","Landscaping","General Arrangement","Other",
];
const STATUSES = ["Current","For Construction","For Review","Draft","Hold / Do Not Use","As-Built","Superseded"];

const CATEGORY_DEFAULT_COLOUR = {
  "Civil":"#2563eb", "Drainage":"#0d9488", "Pavement":"#ea580c",
  "Earthworks":"#a16207", "Utilities":"#7c3aed", "Electrical":"#ca8a04",
  "ITS":"#475569", "Structures":"#64748b", "Landscaping":"#16a34a",
  "Traffic":"#dc2626", "Temporary Works":"#db2777", "As-Built":"#059669",
  "Other":"#94a3b8",
};
const CATEGORIES = Object.keys(CATEGORY_DEFAULT_COLOUR);

export default function Plans() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState({
    project_id: "all", discipline: "all", category: "all", status: "all", uploaded_by: "all", linked: "all",
  });
  const [sortBy, setSortBy] = useState("uploaded_at");

  const reload = () => {
    const params = {};
    Object.entries(filters).forEach(([k,v]) => { if (v && v !== "all") params[k] = v; });
    api.get("/plans", { params }).then(r => {
      const data = [...r.data];
      data.sort((a, b) => {
        switch (sortBy) {
          case "title": return (a.title || "").localeCompare(b.title || "");
          case "drawing_number": return (a.drawing_number || "").localeCompare(b.drawing_number || "");
          case "revision": return (a.revision || "").localeCompare(b.revision || "");
          case "category": return (a.category || "").localeCompare(b.category || "");
          case "status": return (a.status || "").localeCompare(b.status || "");
          case "uploaded_at":
          default: return (b.uploaded_at || "").localeCompare(a.uploaded_at || "");
        }
      });
      setPlans(data);
    });
  };
  useEffect(reload, [filters, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/projects").then(r => setProjects(r.data));
    api.get("/users").then(r => setUsers(r.data));
  }, []);

  return (
    <div className="p-4 md:p-8" data-testid="plans-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline mb-1">Module</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">Plans / Lot Maps</h1>
          <p className="mt-1 text-sm text-slate-600">Drawings, plan revisions and lot-area mark-ups.</p>
        </div>
        {can.uploadPlan(user?.role) && (
          <Button onClick={() => setOpen(true)} className="bg-teal-700 hover:bg-teal-800" data-testid="upload-plan-btn">
            <Upload className="mr-2 size-4"/> Upload Plan
          </Button>
        )}
      </div>

      <Card className="mb-4 border-slate-200 p-3">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <FilterSelect label="Project" value={filters.project_id} onChange={v => setFilters({...filters, project_id:v})}
            options={[{value:"all",label:"All"}].concat(projects.map(p => ({value:p.id, label:`${p.number} — ${p.name}`})))}
            testid="filter-project"/>
          <FilterSelect label="Category" value={filters.category} onChange={v => setFilters({...filters, category:v})}
            options={[{value:"all",label:"All"}].concat(CATEGORIES.map(c => ({value:c,label:c})))}
            testid="filter-category"/>
          <FilterSelect label="Discipline (legacy)" value={filters.discipline} onChange={v => setFilters({...filters, discipline:v})}
            options={[{value:"all",label:"All"}].concat(DISCIPLINES.map(d => ({value:d,label:d})))}
            testid="filter-discipline"/>
          <FilterSelect label="Status" value={filters.status} onChange={v => setFilters({...filters, status:v})}
            options={[{value:"all",label:"All"}].concat(STATUSES.map(s => ({value:s,label:s})))}
            testid="filter-status"/>
          <FilterSelect label="Uploaded By" value={filters.uploaded_by} onChange={v => setFilters({...filters, uploaded_by:v})}
            options={[{value:"all",label:"All"}].concat(users.map(u => ({value:u.id,label:u.name})))}
            testid="filter-uploader"/>
          <FilterSelect label="Sort by" value={sortBy} onChange={setSortBy}
            options={[
              {value:"uploaded_at",label:"Upload date (newest)"},
              {value:"title",label:"Drawing name"},
              {value:"drawing_number",label:"Drawing number"},
              {value:"revision",label:"Revision"},
              {value:"category",label:"Category"},
              {value:"status",label:"Status"},
            ]}
            testid="sort-by"/>
        </div>
      </Card>

      {plans.length === 0 ? (
        <Card className="border-dashed border-slate-300 bg-slate-50 p-10 text-center" data-testid="plans-empty">
          <Map className="mx-auto size-10 text-slate-400"/>
          <div className="mt-2 font-heading text-lg font-bold">No plans uploaded yet</div>
          <p className="mt-1 text-sm text-slate-500">Upload a drawing to start marking up lot areas.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {plans.map(p => <PlanCard key={p.id} plan={p} reload={reload}/>)}
        </div>
      )}

      <UploadDialog open={open} onClose={() => setOpen(false)} projects={projects} reload={reload}/>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9" data-testid={testid}><SelectValue/></SelectTrigger>
        <SelectContent>{options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function PlanCard({ plan, reload }) {
  const { user } = useAuth();
  const isPdf = (plan.file_type || "").includes("pdf");
  const canDelete = ["admin", "qa_manager"].includes(user?.role);
  const canEditMeta = ["admin", "qa_manager", "site_engineer"].includes(user?.role);
  const isSuperseded = plan.status === "Superseded";
  const planColour = plan.plan_colour || CATEGORY_DEFAULT_COLOUR[plan.category] || "#94a3b8";
  const [editingMeta, setEditingMeta] = useState(false);
  const download = async () => {
    try {
      const tok = localStorage.getItem("token");
      const API = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${API}/api/plans/${plan.id}/file`, { headers: { Authorization: `Bearer ${tok}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = plan.original_filename || `${plan.drawing_number}-Rev${plan.revision}.${plan.file_ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1500);
    } catch (e) {
      toast.error("Unable to download this file. Please contact admin.");
    }
  };
  const doDelete = async () => {
    if (!window.confirm(`Delete plan "${plan.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/plans/${plan.id}`);
      toast.success("Plan deleted");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };
  return (
    <Card
      className={`relative overflow-hidden border-slate-200 p-4 ${isSuperseded ? "opacity-60" : ""}`}
      data-testid={`plan-card-${plan.drawing_number}`}
    >
      {/* Coloured left strip */}
      <div
        className="absolute left-0 top-0 h-full w-1.5"
        style={{ background: planColour }}
        data-testid={`plan-colour-strip-${plan.drawing_number}`}
      />
      <div className="ml-1">
        <div className="flex items-start justify-between">
          <div>
            <div className="overline text-teal-700">{plan.drawing_number} · Rev {plan.revision}</div>
            <h3 className="mt-1 font-heading text-lg font-bold tracking-tight text-slate-900">{plan.title}</h3>
            <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-slate-600">
              {plan.category && (
                <span
                  className="rounded-full px-2 py-0.5 font-semibold text-white"
                  style={{ background: planColour }}
                  data-testid={`plan-category-badge-${plan.drawing_number}`}
                >
                  {plan.category}
                </span>
              )}
              <span>{plan.discipline}</span>
              <span>·</span>
              <span>{(plan.file_ext || "").toUpperCase()}</span>
            </div>
          </div>
          <StatusBadge status={plan.status}/>
        </div>
        <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
          {isPdf ? <FileText className="size-3.5"/> : <ImageIcon className="size-3.5"/>}
          <span>{plan.uploaded_by_name}</span>
          <span>· {new Date(plan.uploaded_at).toLocaleDateString()}</span>
        </div>
        <div className="mt-2 flex items-center gap-2 text-xs">
          <span className="rounded bg-slate-100 px-2 py-0.5 font-semibold text-slate-700">{plan.markup_count} markups</span>
          <span className="rounded bg-sky-50 px-2 py-0.5 font-semibold text-teal-700">{plan.linked_lot_count} linked lots</span>
          {plan.scale_calibration?.pixels_per_meter && (
            <span className="rounded bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">Scale set</span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
          {canEditMeta && (
            <button onClick={() => setEditingMeta(true)} className="text-xs font-semibold text-slate-700 hover:underline" data-testid={`edit-meta-${plan.drawing_number}`}>Edit colour/category</button>
          )}
          <button onClick={download} className="text-xs font-semibold text-slate-700 hover:underline" data-testid={`download-plan-${plan.drawing_number}`}>Download</button>
          {canDelete && (
            <button onClick={doDelete} className="text-xs font-semibold text-rose-700 hover:underline" data-testid={`delete-plan-${plan.drawing_number}`}>Delete</button>
          )}
          <Link to={`/plans/${plan.id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700 hover:underline" data-testid={`open-plan-${plan.drawing_number}`}>
            Open / View <ArrowUpRight className="size-3.5"/>
          </Link>
        </div>
      </div>
      {editingMeta && <PlanMetaDialog plan={plan} onClose={() => setEditingMeta(false)} onSaved={() => { setEditingMeta(false); reload(); }}/>}
    </Card>
  );
}

function PlanMetaDialog({ plan, onClose, onSaved }) {
  const [category, setCategory] = useState(plan.category || "");
  const [colour, setColour] = useState(plan.plan_colour || CATEGORY_DEFAULT_COLOUR[plan.category] || "#94a3b8");
  const [status, setStatus] = useState(plan.status || "Current");
  const [tag, setTag] = useState(plan.tag || "");
  const [saving, setSaving] = useState(false);
  const onCategoryChange = (v) => {
    setCategory(v);
    // If user has not customised colour yet, snap to preset on category change.
    if (!plan.plan_colour || colour === (CATEGORY_DEFAULT_COLOUR[plan.category] || "")) {
      setColour(CATEGORY_DEFAULT_COLOUR[v] || colour);
    }
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (category) payload.category = category;
      if (colour) payload.plan_colour = colour;
      if (status) payload.status = status;
      if (tag !== plan.tag) payload.tag = tag;
      await api.patch(`/plans/${plan.id}/meta`, payload);
      toast.success("Plan updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="plan-meta-dialog">
        <DialogHeader><DialogTitle>Plan colour & category</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={onCategoryChange}>
              <SelectTrigger data-testid="plan-meta-category"><SelectValue placeholder="Pick a category"/></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Colour</Label>
              <Input type="color" value={colour} onChange={e => setColour(e.target.value)} className="h-10" data-testid="plan-meta-colour"/>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="plan-meta-status"><SelectValue/></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Tag</Label>
            <Input value={tag} onChange={e => setTag(e.target.value)} placeholder="e.g. Stage 1" data-testid="plan-meta-tag"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="plan-meta-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UploadDialog({ open, onClose, projects, reload }) {
  const fileRef = useRef();
  const [mode, setMode] = useState("single"); // 'single' or 'bulk'
  const [form, setForm] = useState({
    project_id: "", title: "", drawing_number: "", revision: "1",
    discipline: "Drainage", notes: "", supersedes_id: "",
  });
  const [file, setFile] = useState(null);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [supersedeOptions, setSupersedeOptions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null); // { uploaded, failed }

  useEffect(() => {
    if (!form.project_id) { setSupersedeOptions([]); return; }
    api.get("/plans", { params: { project_id: form.project_id } }).then(r => setSupersedeOptions(r.data));
  }, [form.project_id]);

  const submit = async () => {
    if (mode === "single") {
      if (!file || !form.project_id || !form.title || !form.drawing_number) {
        toast.error("File, project, title and drawing number are required"); return;
      }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
        await api.post("/plans", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Plan uploaded");
        onClose(); reload();
        setForm({ project_id: "", title: "", drawing_number: "", revision: "1", discipline: "Drainage", notes: "", supersedes_id: "" });
        setFile(null);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Upload failed");
      } finally { setUploading(false); }
    } else {
      if (!bulkFiles.length || !form.project_id) {
        toast.error("Project and at least one file are required"); return;
      }
      setUploading(true); setResults(null);
      try {
        const fd = new FormData();
        bulkFiles.forEach(f => fd.append("files", f));
        fd.append("project_id", form.project_id);
        fd.append("discipline", form.discipline);
        fd.append("revision", form.revision);
        if (form.notes) fd.append("notes", form.notes);
        const r = await api.post("/plans/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setResults(r.data);
        if (r.data.uploaded_count) toast.success(`${r.data.uploaded_count} plan(s) uploaded`);
        if (r.data.failed_count) toast.error(`${r.data.failed_count} file(s) failed — see details`);
        reload();
        if (!r.data.failed_count) {
          setBulkFiles([]);
          setForm({ project_id: "", title: "", drawing_number: "", revision: "1", discipline: "Drainage", notes: "", supersedes_id: "" });
          setTimeout(() => { onClose(); setResults(null); }, 1200);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Bulk upload failed");
      } finally { setUploading(false); }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="upload-plan-dialog">
        <DialogHeader><DialogTitle>Upload Plans / Drawings</DialogTitle></DialogHeader>
        <div className="mb-2 inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5 text-xs" data-testid="upload-mode-toggle">
          <button type="button" onClick={() => setMode("single")} className={`rounded px-3 py-1 font-semibold ${mode === "single" ? "bg-white text-teal-800 shadow-sm" : "text-slate-600"}`} data-testid="upload-mode-single">Single (with metadata)</button>
          <button type="button" onClick={() => setMode("bulk")} className={`rounded px-3 py-1 font-semibold ${mode === "bulk" ? "bg-white text-teal-800 shadow-sm" : "text-slate-600"}`} data-testid="upload-mode-bulk">Bulk (multiple files)</button>
        </div>
        <div className="space-y-3">
          <div>
            <Label>Project *</Label>
            <Select value={form.project_id} onValueChange={v => setForm({...form, project_id: v})}>
              <SelectTrigger data-testid="upload-project"><SelectValue placeholder="Select project"/></SelectTrigger>
              <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.number} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {mode === "single" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Drawing Title *</Label><Input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} data-testid="upload-title"/></div>
              <div><Label>Drawing Number *</Label><Input value={form.drawing_number} onChange={e=>setForm({...form,drawing_number:e.target.value})} placeholder="DRG-CV-401" data-testid="upload-number"/></div>
              <div><Label>Revision</Label><Input value={form.revision} onChange={e=>setForm({...form,revision:e.target.value})} data-testid="upload-revision"/></div>
              <div>
                <Label>Discipline</Label>
                <Select value={form.discipline} onValueChange={v=>setForm({...form,discipline:v})}>
                  <SelectTrigger data-testid="upload-discipline"><SelectValue/></SelectTrigger>
                  <SelectContent>{DISCIPLINES.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          {mode === "bulk" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Discipline (applied to all)</Label>
                <Select value={form.discipline} onValueChange={v=>setForm({...form,discipline:v})}>
                  <SelectTrigger data-testid="upload-bulk-discipline"><SelectValue/></SelectTrigger>
                  <SelectContent>{DISCIPLINES.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Revision (applied to all)</Label><Input value={form.revision} onChange={e=>setForm({...form,revision:e.target.value})} data-testid="upload-bulk-revision"/></div>
            </div>
          )}
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} rows={2} data-testid="upload-notes"/>
          </div>
          {mode === "single" && supersedeOptions.length > 0 && (
            <div>
              <Label>Is this a new revision of an existing plan?</Label>
              <Select value={form.supersedes_id || "none"} onValueChange={v=>setForm({...form,supersedes_id: v === "none" ? "" : v})}>
                <SelectTrigger data-testid="upload-supersedes"><SelectValue placeholder="No"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No, this is a new plan</SelectItem>
                  {supersedeOptions.map(p => <SelectItem key={p.id} value={p.id}>Supersedes {p.drawing_number} Rev {p.revision}</SelectItem>)}
                </SelectContent>
              </Select>
              {form.supersedes_id && <p className="mt-1 text-xs text-orange-700">The old plan will be marked Superseded. You can copy its markups onto this revision from the plan viewer.</p>}
            </div>
          )}
          <div>
            <Label>{mode === "bulk" ? "Files (PDF, PNG, JPG) — multiple *" : "File (PDF, PNG, JPG) *"}</Label>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,image/png,image/jpeg"
              multiple={mode === "bulk"}
              onChange={e => {
                if (mode === "bulk") setBulkFiles(Array.from(e.target.files || []));
                else setFile(e.target.files?.[0]);
              }}
              data-testid="upload-file"
              className="block w-full text-sm"
            />
            {mode === "bulk" && bulkFiles.length > 0 && (
              <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2 text-xs" data-testid="bulk-file-preview">
                <div className="font-semibold text-slate-700">{bulkFiles.length} file(s) selected:</div>
                <ul className="mt-1 max-h-32 overflow-auto">
                  {bulkFiles.map((f, i) => (
                    <li key={i} className="truncate text-slate-600">· {f.name} <span className="text-slate-400">({Math.round(f.size/1024)} KB)</span></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {results && results.failed_count > 0 && (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-900" data-testid="bulk-failed-list">
              <div className="font-semibold">Failed:</div>
              <ul className="mt-1">{results.failed.map((f, i) => <li key={i}>· <b>{f.filename}</b>: {f.reason}</li>)}</ul>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={uploading} className="bg-teal-700 hover:bg-teal-800" data-testid="upload-submit">
            <FilePlus className="mr-1.5 size-4"/>{uploading ? "Uploading…" : (mode === "bulk" ? `Upload ${bulkFiles.length || ""} Plan${bulkFiles.length === 1 ? "" : "s"}` : "Upload Plan")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
